/**
 * Browser Notifications helper for VaultNotes reminders
 */

/**
 * Check if notifications are supported
 */
export function isNotificationSupported() {
  return 'Notification' in window;
}

/**
 * Get current notification permission status
 */
export function getNotificationPermission() {
  if (!isNotificationSupported()) return 'unsupported';
  return Notification.permission;
}

/**
 * Request notification permission from user
 */
export async function requestNotificationPermission() {
  if (!isNotificationSupported()) {
    console.warn('[Notifications] Not supported in this browser');
    return 'unsupported';
  }

  if (Notification.permission === 'granted') {
    return 'granted';
  }

  if (Notification.permission === 'denied') {
    return 'denied';
  }

  const permission = await Notification.requestPermission();
  return permission;
}

/**
 * Show a browser notification
 */
export function showNotification(title, options = {}) {
  if (!isNotificationSupported()) {
    console.warn('[Notifications] Not supported');
    return null;
  }

  if (Notification.permission !== 'granted') {
    console.warn('[Notifications] Permission not granted');
    return null;
  }

  const defaultOptions = {
    icon: '/favicon.ico',
    badge: '/favicon.ico',
    vibrate: [100, 50, 100],
    requireInteraction: false,
    tag: options.tag || 'vaultnotes-notification',
    ...options,
  };

  try {
    const notification = new Notification(title, defaultOptions);
    
    notification.onclick = () => {
      window.focus();
      notification.close();
      options.onClick?.();
    };

    notification.onclose = () => {
      options.onClose?.();
    };

    return notification;
  } catch (error) {
    console.error('[Notifications] Failed to show notification:', error);
    return null;
  }
}

/**
 * Schedule a notification using service worker (for background notifications)
 */
export async function scheduleNotification(title, options = {}, delayMs = 0) {
  if (!('serviceWorker' in navigator)) {
    console.warn('[Notifications] Service Worker not supported');
    return false;
  }

  const registration = await navigator.serviceWorker.ready;
  
  if (!registration.showNotification) {
    console.warn('[Notifications] showNotification not supported');
    return false;
  }

  // If no delay, show immediately via service worker
  if (delayMs === 0) {
    await registration.showNotification(title, {
      icon: '/favicon.ico',
      badge: '/favicon.ico',
      vibrate: [100, 50, 100],
      tag: options.tag || 'vaultnotes-reminder',
      ...options,
    });
    return true;
  }

  // For delayed notifications, we use setTimeout (basic approach)
  // In production, you'd want to use Push API with a backend
  setTimeout(async () => {
    await registration.showNotification(title, {
      icon: '/favicon.ico',
      badge: '/favicon.ico',
      vibrate: [100, 50, 100],
      tag: options.tag || 'vaultnotes-reminder',
      ...options,
    });
  }, delayMs);

  return true;
}

/**
 * Check and show due reminders
 */
export function checkDueReminders(reminders, onReminderDue) {
  if (!isNotificationSupported() || Notification.permission !== 'granted') {
    return;
  }

  const now = new Date();
  const dueReminders = reminders.filter((reminder) => {
    if (reminder.is_completed) return false;
    const dueTime = new Date(reminder.due_at);
    const diff = dueTime - now;
    // Due within the next minute and not more than 5 minutes past
    return diff <= 60000 && diff >= -300000;
  });

  dueReminders.forEach((reminder) => {
    showNotification('VaultNotes Reminder', {
      body: reminder.title || 'You have a reminder!',
      tag: `reminder-${reminder.id}`,
      data: { reminderId: reminder.id },
      onClick: () => onReminderDue?.(reminder),
    });
  });

  return dueReminders;
}

/**
 * Format time remaining for a reminder
 */
export function formatTimeRemaining(dueAt) {
  const now = new Date();
  const due = new Date(dueAt);
  const diff = due - now;

  if (diff < 0) return 'Overdue';
  if (diff < 60000) return 'Due now';
  if (diff < 3600000) return `In ${Math.ceil(diff / 60000)} min`;
  if (diff < 86400000) return `In ${Math.ceil(diff / 3600000)} hours`;
  return `In ${Math.ceil(diff / 86400000)} days`;
}
